import React, { useEffect, useMemo } from "react";
import { motion, useAnimation } from "framer-motion";

const TierAnimation = ({
  tierName = "Gold",
  currentPoint = 850,
  highestTierMinPoint = 1000,
}) => {
  const progress = Math.min(currentPoint / highestTierMinPoint, 1);

  const center = 150;
  const strokeWidth = 8;
  const strokeWidthInner = 5;

  const innerRadius = 60;

  const tickRadius = 95;

  const outerRadius = 130;

  const fullCircumference = 2 * Math.PI * outerRadius;

  const arcFraction = 0.85;
  const arcLength = fullCircumference * arcFraction;
  const emptySpace = fullCircumference - arcLength;

  const startAngleDeg = 329;
  const startAngleRad = (startAngleDeg * Math.PI) / 180;
  const arcAngleRad = arcFraction * 2 * Math.PI;

  const controls = useAnimation();

  const dashOffset = useMemo(() => arcLength * (1 - progress), [arcLength, progress]);

  useEffect(() => {
    controls.start({ strokeDashoffset: dashOffset });
  }, [dashOffset, controls]);

  const endAngle = startAngleRad + progress * arcAngleRad;
  const endCx = center + outerRadius * Math.cos(endAngle);
  const endCy = center + outerRadius * Math.sin(endAngle);

  return (
    <div style={{ width: 320, height: 320, borderRadius: "50%" }}>
      <svg width={320} height={320}>
      <defs>
          <linearGradient id="gradientStrokeInner" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#eee" />
            <stop offset="100%" stopColor="grey" />
          </linearGradient>
        </defs>

        <circle
          cx={center}
          cy={center}
          r={innerRadius}
          fill="none"
          stroke="url(#gradientStrokeInner)"
          strokeWidth={strokeWidthInner}
        />

        {Array.from({ length: 40 }).map((_, i) => {
          const angle = (i / 40) * 2 * Math.PI;
          const x1 = center + (tickRadius - 5) * Math.cos(angle);
          const y1 = center + (tickRadius - 5) * Math.sin(angle);
          const x2 = center + (tickRadius + 5) * Math.cos(angle);
          const y2 = center + (tickRadius + 5) * Math.sin(angle);
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="#ccc"
              strokeWidth="2"
            />
          );
        })}

        <defs>
          <linearGradient id="gradientStroke" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#11CA92" />
            <stop offset="25%" stopColor="#129eee" />
            <stop offset="50%" stopColor="#975ec8" />
            <stop offset="100%" stopColor="#e94779" />
          </linearGradient>
        </defs>

        <motion.circle
          cx={center}
          cy={center}
          r={outerRadius}
          fill="none"
          stroke="url(#gradientStroke)"
          strokeWidth={strokeWidth}
          strokeDasharray={`${arcLength} ${emptySpace}`}
          strokeDashoffset={arcLength}
          strokeLinecap="round"
          transform={`rotate(${startAngleDeg} ${center} ${center})`}
          animate={controls}
          transition={{ duration: 1.6, ease: "easeOut" }}
        />

        {progress > 0 && (
          <circle cx={endCx} cy={endCy} r={10} fill="#515459" />
        )}

        <text
          x={center}
          y={center}
          textAnchor="middle"
          alignmentBaseline="middle"
          fontSize="22"
          fill="#515459"
          fontWeight="500"
        >
          {tierName}
        </text>
      </svg>
    </div>
  );
};

export default TierAnimation;

